import {ChevronDown} from "lucide-react"
import {DropdownMenu as DropdownMenuPrimitive} from "radix-ui"
import type React from "react"

import {cn} from "@/lib/cn"

/**
 * DIZED pulldown per the style guide: pill trigger with chevron cap,
 * gradient panel with row hover states (e.g. language selector).
 */
export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

/** Pill-shaped trigger matching the "PULL DOWN" mockup. */
export const DropdownPillTrigger = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>) => (
  <DropdownMenuPrimitive.Trigger
    className={cn(
      "group inline-flex h-8 items-center overflow-hidden rounded-pill p-0",
      "appearance-none bg-transparent border border-solid border-secondary-border",
      "font-display text-sm font-bold uppercase tracking-wide text-secondary-foreground",
      "transition-colors hover:border-ring",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      className,
    )}
    {...props}
  >
    <span className="px-4">{children}</span>
    <span className="flex h-full w-8 items-center justify-center bg-action text-action-foreground">
      <ChevronDown className="size-3.5 transition-transform duration-150 group-data-[state=open]:rotate-180" />
    </span>
  </DropdownMenuPrimitive.Trigger>
)

export const DropdownMenuContent = ({
  className,
  sideOffset = 6,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      sideOffset={sideOffset}
      className={cn(
        "z-50 min-w-40 overflow-hidden rounded-list card-elev p-1.5 text-foreground shadow-card",
        "origin-(--radix-dropdown-menu-content-transform-origin)",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
        "data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
)

export const DropdownMenuItem = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>) => (
  <DropdownMenuPrimitive.Item
    className={cn(
      "cursor-pointer select-none rounded-mini px-3 py-2 font-sans text-sm outline-none",
      "data-[highlighted]:bg-action data-[highlighted]:text-action-foreground",
      className,
    )}
    {...props}
  />
)

export const DropdownMenuSeparator = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>) => (
  <DropdownMenuPrimitive.Separator className={cn("my-1 h-px bg-edge/50", className)} {...props} />
)
